import {
  App,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router';
import {
  hrDepthApi,
  type LeaveQuota,
  type LeaveRequest,
  type LeaveStatus,
  type LeaveType,
} from '@/api/hr-depth';
import { hrApi } from '@/api/hr';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';

const STATUS_COLOR: Record<LeaveStatus, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  REJECTED: 'red',
  CANCELLED: 'default',
};

export function HrLeavePage() {
  const [search, setSearch] = useSearchParams();
  const tab = search.get('tab') ?? 'requests';
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Leave"
        description="Leave types, quotas per employment type, requests with approval workflow, and per-staff balances."
      />
      <Tabs
        activeKey={tab}
        onChange={(k) => setSearch({ tab: k })}
        items={[
          { key: 'requests', label: 'Requests', children: <RequestsTab /> },
          { key: 'balances', label: 'Balances', children: <BalancesTab /> },
          { key: 'types', label: 'Types', children: <TypesTab /> },
          { key: 'quotas', label: 'Quotas', children: <QuotasTab /> },
        ]}
      />
    </div>
  );
}

/* ----------------- Types tab ----------------- */
interface TypeForm {
  name: string;
  paid: boolean;
  allowCarryForward: boolean;
  maxCarryForward?: number;
}
function TypesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<TypeForm>();

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'leave', 'types', 'all'],
    queryFn: () => hrDepthApi.listLeaveTypes(false),
  });

  const upsert = useMutation({
    mutationFn: (v: TypeForm) =>
      editing
        ? hrDepthApi.updateLeaveType(editing.id, {
            name: v.name.trim(),
            paid: v.paid,
            allowCarryForward: v.allowCarryForward,
            maxCarryForward: v.allowCarryForward ? v.maxCarryForward ?? null : null,
          })
        : hrDepthApi.createLeaveType({
            name: v.name.trim(),
            paid: v.paid,
            allowCarryForward: v.allowCarryForward,
            maxCarryForward: v.allowCarryForward ? v.maxCarryForward ?? null : null,
          }),
    onSuccess: () => {
      message.success(editing ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['hr', 'leave', 'types'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrDepthApi.toggleLeaveType(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'leave', 'types'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const watchedCarry = Form.useWatch('allowCarryForward', form);

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ paid: true, allowCarryForward: false });
  };
  const openEdit = (row: LeaveType) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      paid: row.paid,
      allowCarryForward: row.allowCarryForward,
      maxCarryForward: row.maxCarryForward ?? undefined,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<LeaveType>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Paid',
      dataIndex: 'paid',
      width: 90,
      render: (p: boolean) => <Tag color={p ? 'success' : 'default'}>{p ? 'Paid' : 'Unpaid'}</Tag>,
    },
    {
      title: 'Carry forward',
      width: 200,
      render: (_, row) =>
        row.allowCarryForward ? (
          <Tag color="processing">
            Yes{row.maxCarryForward != null ? ` · max ${row.maxCarryForward}d` : ''}
          </Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button> : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {canCreate && (
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New leave type</Button>
        </Space>
      )}
      <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />

      <Modal
        title={editing ? `Edit · ${editing.name}` : 'New leave type'}
        open={open}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Casual, Sick, Annual" />
            </Form.Item>
            <Form.Item label="Paid" name="paid" valuePropName="checked" tooltip="Unpaid leave triggers a payroll deduction">
              <Switch />
            </Form.Item>
            <Form.Item label="Allow carry forward" name="allowCarryForward" valuePropName="checked">
              <Switch />
            </Form.Item>
            {watchedCarry && (
              <Form.Item
                label="Max carry forward (days)"
                name="maxCarryForward"
                tooltip="Cap on unused days that roll into next year. Blank = no cap."
              >
                <InputNumber style={{ width: '100%' }} min={0} max={366} step={1} />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>
    </div>
  );
}

/* ----------------- Quotas tab ----------------- */
interface QuotaForm {
  leaveTypeId: number;
  employmentTypeId: number;
  daysPerYear: number;
}
function QuotasTab() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('HR_MGMT:DELETE'));

  const [editing, setEditing] = useState<LeaveQuota | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<QuotaForm>();

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'leave', 'quotas'],
    queryFn: () => hrDepthApi.listLeaveQuotas(),
  });
  const { data: types } = useQuery({
    queryKey: ['hr', 'leave', 'types', 'active'],
    queryFn: () => hrDepthApi.listLeaveTypes(true),
    staleTime: 60_000,
  });
  const { data: empTypes } = useQuery({
    queryKey: ['setup', 'lookups', 'employment-types', 'active'],
    queryFn: () => setupApi.listLookup('employment-types', true),
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (v: QuotaForm) =>
      editing
        ? hrDepthApi.updateLeaveQuota(editing.id, v)
        : hrDepthApi.createLeaveQuota(v),
    onSuccess: () => {
      message.success(editing ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['hr', 'leave', 'quotas'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrDepthApi.toggleLeaveQuota(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'leave', 'quotas'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => hrDepthApi.deleteLeaveQuota(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'leave', 'quotas'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: LeaveQuota) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      leaveTypeId: row.leaveTypeId,
      employmentTypeId: row.employmentTypeId,
      daysPerYear: row.daysPerYear,
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<LeaveQuota>['columns'] = [
    { title: 'Leave type', dataIndex: 'leaveTypeName' },
    { title: 'Employment type', dataIndex: 'employmentTypeName' },
    {
      title: 'Days / year',
      dataIndex: 'daysPerYear',
      width: 130,
      align: 'right',
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 160,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          {canEdit && <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />}
          {canDelete && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                modal.confirm({
                  title: 'Remove this quota?',
                  okType: 'danger',
                  onOk: () => remove.mutate(row.id),
                })
              }
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {canCreate && (
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add quota</Button>
        </Space>
      )}
      <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />

      <Modal
        title={editing ? 'Edit quota' : 'Add quota'}
        open={open}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Leave type" name="leaveTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={!!editing}
                options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
            <Form.Item label="Employment type" name="employmentTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={!!editing}
                options={(empTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
            <Form.Item label="Days per year" name="daysPerYear" rules={[{ required: true, type: 'number', min: 0 }]}>
              <InputNumber style={{ width: '100%' }} min={0} max={366} step={1} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

/* ----------------- Requests tab ----------------- */
interface RequestForm {
  staffId: number;
  leaveTypeId: number;
  range: [Dayjs, Dayjs];
  totalDays: number;
  reason?: string;
}
function RequestsTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canApprove = useAuthStore((s) => s.hasAuthority('HR_MGMT:APPROVE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));
  const askReason = useReasonPrompt();

  const [statusFilter, setStatusFilter] = useState<LeaveStatus | undefined>(undefined);
  const [staffFilter, setStaffFilter] = useState<number | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<RequestForm>();

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'leave', 'requests', { statusFilter, staffFilter }],
    queryFn: () =>
      hrDepthApi.listLeaveRequests({ status: statusFilter, staffId: staffFilter }),
  });

  const { data: types } = useQuery({
    queryKey: ['hr', 'leave', 'types', 'active'],
    queryFn: () => hrDepthApi.listLeaveTypes(true),
    staleTime: 60_000,
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const create = useMutation({
    mutationFn: (v: RequestForm) =>
      hrDepthApi.createLeaveRequest({
        staffId: v.staffId,
        leaveTypeId: v.leaveTypeId,
        fromDate: v.range[0].format('YYYY-MM-DD'),
        toDate: v.range[1].format('YYYY-MM-DD'),
        totalDays: Number(v.totalDays).toFixed(1),
        reason: v.reason?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Leave request filed');
      qc.invalidateQueries({ queryKey: ['hr', 'leave', 'requests'] });
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const approve = useMutation({
    mutationFn: (id: number) => hrDepthApi.approveLeaveRequest(id),
    onSuccess: () => {
      message.success('Approved · balance decremented');
      qc.invalidateQueries({ queryKey: ['hr', 'leave'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => hrDepthApi.rejectLeaveRequest(id, reason),
    onSuccess: () => {
      message.success('Rejected');
      qc.invalidateQueries({ queryKey: ['hr', 'leave'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const cancel = useMutation({
    mutationFn: (id: number) => hrDepthApi.cancelLeaveRequest(id),
    onSuccess: () => {
      message.success('Cancelled');
      qc.invalidateQueries({ queryKey: ['hr', 'leave'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ totalDays: 1 });
  };

  const watchedRange = Form.useWatch('range', form);
  useEffect(() => {
    if (watchedRange && watchedRange[0] && watchedRange[1]) {
      const days = watchedRange[1].diff(watchedRange[0], 'day') + 1;
      form.setFieldValue('totalDays', days);
    }
  }, [watchedRange, form]);

  const columns: TableProps<LeaveRequest>['columns'] = [
    {
      title: 'Filed',
      dataIndex: 'createdAt',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Staff',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.staffName}</Typography.Text>
          <Space size={6}>
            <Tag>{row.leaveTypeName}</Tag>
            {!row.paid && <Tag color="warning">Unpaid</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Period',
      width: 230,
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text>{dayjs(row.fromDate).format('D MMM')} – {dayjs(row.toDate).format('D MMM YYYY')}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.totalDays} day(s)</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: LeaveStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: 'Actions',
      width: 230,
      align: 'right',
      render: (_, row) =>
        row.status === 'PENDING' ? (
          <Space size={4}>
            {canApprove && (
              <Button
                size="small"
                type="text"
                icon={<CheckOutlined />}
                onClick={() => approve.mutate(row.id)}
                loading={approve.isPending}
              >
                Approve
              </Button>
            )}
            {canApprove && (
              <Button
                size="small"
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={async () => {
                  const reason = await askReason({
                    title: 'Reject leave request?',
                    placeholder: 'Why is this request being rejected?',
                    okText: 'Reject',
                    danger: true,
                  });
                  if (reason) reject.mutate({ id: row.id, reason });
                }}
              >
                Reject
              </Button>
            )}
            {canEdit && (
              <Button size="small" type="text" icon={<StopOutlined />} onClick={() => cancel.mutate(row.id)}>
                Cancel
              </Button>
            )}
          </Space>
        ) : row.status === 'APPROVED' && canEdit ? (
          <Button size="small" type="text" icon={<StopOutlined />} onClick={() => cancel.mutate(row.id)}>
            Cancel
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Space wrap size={12}>
        <Select
          allowClear
          placeholder="Status"
          style={{ minWidth: 160 }}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as LeaveStatus[]).map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Staff"
          style={{ minWidth: 240 }}
          value={staffFilter}
          onChange={(v) => setStaffFilter(v)}
          options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
        />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            File request
          </Button>
        )}
      </Space>

      <Table rowKey="id" size="middle" loading={isFetching} columns={columns} dataSource={data ?? []} pagination={{ pageSize: 25 }} />

      <Modal
        title="File leave request"
        open={creating}
        onCancel={() => setCreating(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        okText="File"
        destroyOnHidden
        width={520}
      >
        {creating && (
          <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
            <Form.Item label="Staff" name="staffId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item label="Leave type" name="leaveTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
            <Form.Item label="From – to" name="range" rules={[{ required: true }]}>
              <DatePicker.RangePicker format="DD MMM YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Total days"
              name="totalDays"
              rules={[{ required: true, type: 'number', min: 0.5 }]}
              tooltip="Auto-filled from range. Override for half-days (0.5)."
            >
              <InputNumber style={{ width: '100%' }} min={0.5} step={0.5} />
            </Form.Item>
            <Form.Item label="Reason" name="reason" rules={[{ max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

/* ----------------- Balances tab ----------------- */
function BalancesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [academicYearId, setAcademicYearId] = useState<number | undefined>(undefined);
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocForm] = Form.useForm<{ carryForward: boolean; previousAcademicYearId?: number }>();

  const { data: years } = useQuery({
    queryKey: ['setup', 'academic-years'],
    queryFn: () => setupApi.listAcademicYears(),
  });

  useEffect(() => {
    if (academicYearId == null && years && years.length > 0) {
      const current = years.find((y) => y.current);
      setAcademicYearId(current?.id ?? years[0].id);
    }
  }, [years, academicYearId]);

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'leave', 'balances', academicYearId],
    queryFn: () => hrDepthApi.yearBalances(academicYearId!),
    enabled: academicYearId != null,
  });

  const allocate = useMutation({
    mutationFn: (v: { carryForward: boolean; previousAcademicYearId?: number }) =>
      hrDepthApi.allocateBalances({
        academicYearId: academicYearId!,
        carryForward: v.carryForward,
        previousAcademicYearId: v.carryForward ? v.previousAcademicYearId ?? null : null,
      }),
    onSuccess: (n) => {
      message.success(`Allocated ${n} balance rows`);
      qc.invalidateQueries({ queryKey: ['hr', 'leave', 'balances'] });
      setAllocOpen(false);
      allocForm.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedCarry = Form.useWatch('carryForward', allocForm);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Academic year"
          style={{ minWidth: 220 }}
          value={academicYearId}
          onChange={(v) => setAcademicYearId(v)}
          options={(years ?? []).map((y) => ({
            value: y.id,
            label: `${y.name}${y.current ? ' (current)' : ''}`,
          }))}
        />
        {canEdit && academicYearId != null && (
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => {
              allocForm.resetFields();
              allocForm.setFieldsValue({ carryForward: false });
              setAllocOpen(true);
            }}
          >
            Allocate balances
          </Button>
        )}
      </Space>

      {academicYearId == null ? (
        <Empty description="Pick an academic year" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          dataSource={data ?? []}
          pagination={{ pageSize: 50 }}
          columns={[
            { title: 'Staff', dataIndex: 'staffName' },
            { title: 'Leave type', dataIndex: 'leaveTypeName', width: 200 },
            { title: 'Allocated', dataIndex: 'allocatedDays', width: 110, align: 'right' },
            {
              title: 'Carried',
              dataIndex: 'carriedForward',
              width: 100,
              align: 'right',
              render: (v: string) => Number(v) > 0 ? <Tag color="processing">{v}</Tag> : <span style={{ opacity: 0.4 }}>0</span>,
            },
            { title: 'Used', dataIndex: 'usedDays', width: 90, align: 'right' },
            {
              title: 'Remaining',
              dataIndex: 'remainingDays',
              width: 120,
              align: 'right',
              render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
            },
          ]}
        />
      )}

      <Drawer
        open={allocOpen}
        onClose={() => setAllocOpen(false)}
        title="Allocate leave balances"
        size={460}
        extra={
          <Space>
            <Button onClick={() => setAllocOpen(false)}>Cancel</Button>
            <Button type="primary" loading={allocate.isPending} onClick={() => allocForm.submit()}>
              Allocate
            </Button>
          </Space>
        }
        destroyOnHidden
      >
        {allocOpen && (
          <Form form={allocForm} layout="vertical" onFinish={(v) => allocate.mutate(v)}>
            <Typography.Paragraph type="secondary">
              Creates / updates balance rows for every active staff × every active leave type using the quota for each
              staff's employment type.
            </Typography.Paragraph>
            <Form.Item label="Carry forward unused days from a previous year" name="carryForward" valuePropName="checked">
              <Switch />
            </Form.Item>
            {watchedCarry && (
              <Form.Item label="Previous academic year" name="previousAcademicYearId" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(years ?? [])
                    .filter((y) => y.id !== academicYearId)
                    .map((y) => ({ value: y.id, label: y.name }))}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Drawer>
    </div>
  );
}
